import { useCallback } from 'react';

import { LogLevel } from 'features/analytics/enums';
import { sendAnalytics } from 'features/analytics/store';
import { useAppDispatch } from 'features/app/hooks';
import { useMediaStream } from 'features/media-streams/hooks';
import type { RtcConnection } from 'features/sip/interfaces';

export const useLocalVideo = () => {
  const { getVideoStream, getPrivacyMediaStreamTrack } = useMediaStream();
  const dispatch = useAppDispatch();

  const setLocalVideoPrivacy = async (rtcConnection: RtcConnection) => {
    const [, localRtpVideo] = rtcConnection.getSenders();
    localRtpVideo.track?.stop();

    await localRtpVideo.replaceTrack(getPrivacyMediaStreamTrack());
  };

  const setLocalVideo = async (rtcConnection: RtcConnection) => {
    const senders = rtcConnection.getSenders();
    const localRtpVideo = senders.find((s) => s.track?.kind === 'video');
    localRtpVideo?.track?.stop();

    const stream = await getVideoStream();
    const [localVideoTrack] = stream.getVideoTracks();

    await localRtpVideo?.replaceTrack(localVideoTrack);
  };

  const stopRtcTracksAndCloseConnection = useCallback(
    (rtcConnection: RtcConnection) => {
      // stop the local video feeds
      rtcConnection.getSenders().forEach((sender) => {
        sender.track?.stop();
      });
      // stop the incoming video streams
      rtcConnection.getReceivers().forEach((receiver) => {
        receiver.track?.stop();
      });
      if (rtcConnection.signalingState !== 'closed') {
        dispatch(
          sendAnalytics({
            Method: 'stopRtcTracksAndCloseConnection',
            Message: 'RtcConnection is not closed at this point, closing it',
            Level: LogLevel.WARNING,
          })
        );
        rtcConnection.close();
      }
    },
    [dispatch]
  );

  return {
    setLocalVideoPrivacy,
    setLocalVideo,
    stopRtcTracksAndCloseConnection,
  };
};
